<template>
  <div id="app">
    <aside class="sidebar">
      <button class="brand" @click="go('create')" aria-label="进入创作台">
        <span class="brand-mark">A</span><strong>ARC<span>AI</span></strong>
      </button>
      <nav>
        <button :class="{ active: page === 'create' }" @click="go('create')"><span class="nav-icon">✦</span>创作台</button>
        <button :class="{ active: page === 'gallery' }" @click="go('gallery')"><span class="nav-icon">▦</span>我的作品</button>
        <button :class="{ active: page === 'pricing' }" @click="go('pricing')"><span class="nav-icon">◇</span>价格方案</button>
      </nav>
      <div class="sidebar-bottom">
        <button class="credits" @click="go('pricing')">
          <span><small>可用算力</small><strong>{{ user ? user.money || user.score || 0 : '--' }}</strong></span>
          <span class="plus">+</span>
        </button>
        <div v-if="user" class="account">
          <div class="avatar">{{ displayName.slice(0, 1).toUpperCase() }}</div>
          <span><strong>{{ displayName }}</strong><small>{{ user.email || user.mobile || '创作者' }}</small></span>
          <button class="icon-button" title="退出登录" @click="logout">↗</button>
        </div>
        <button v-else class="login-entry" @click="openAuth('login')">登录 / 注册</button>
      </div>
    </aside>

    <main>
      <header class="topbar">
        <div>
          <small>{{ page === 'create' ? 'AI IMAGE STUDIO' : page === 'gallery' ? 'MY LIBRARY' : 'MEMBERSHIP' }}</small>
          <h1>{{ pageTitle }}</h1>
        </div>
        <div class="header-actions">
          <span class="status"><i></i>服务正常</span>
          <button v-if="!user" class="button ghost" @click="openAuth('login')">登录</button>
          <button v-if="!user" class="button primary" @click="openAuth('register')">免费注册</button>
        </div>
      </header>
      <div class="mobile-desktop-notice" role="note">
        <span>为了获得更好的创作体验，建议使用电脑访问并使用</span>
      </div>

      <section v-if="page === 'create'" class="workspace">
        <div class="control-panel">
          <div class="panel-title"><span>{{ form.mode === 'edit' ? '编辑设置' : '生成设置' }}</span><small>本次消耗 {{ formattedTotalCost }} 算力</small></div>
          <div class="mode-switch" role="tablist" aria-label="创作模式">
            <button v-for="mode in modes" :key="mode.value" :class="{ selected: form.mode === mode.value }" role="tab" :aria-selected="form.mode === mode.value" @click="setMode(mode.value)">{{ mode.label }}</button>
          </div>
          <label v-if="form.mode === 'edit'" class="field upload-field">
            <span>参考图片 <em>JPG / PNG / WEBP，最大 10MB</em></span>
            <input ref="imageInput" class="file-input" type="file" accept="image/jpeg,image/png,image/webp" @change="selectImage" />
            <button v-if="!imagePreview" class="upload-box" type="button" @click="$refs.imageInput.click()"><b>＋</b><span>上传待编辑图片</span></button>
            <div v-else class="upload-preview"><img :src="imagePreview" alt="待编辑图片预览" /><div><span>{{ sourceImage.name }}</span><button type="button" @click="$refs.imageInput.click()">重新选择</button></div></div>
          </label>
          <label class="field">
            <span>创意描述 <em>{{ form.prompt.length }}/800</em></span>
            <textarea v-model.trim="form.prompt" maxlength="800" placeholder="描述你想创造的画面，例如：雨夜中的未来都市，霓虹灯倒映在街道上，电影感构图..."></textarea>
          </label>
          <div class="prompt-actions">
            <button @click="randomPrompt">✦ 灵感</button>
            <button @click="form.prompt = ''">清空</button>
          </div>
          <div class="field">
            <span>清晰度 <em>单张价格</em></span>
            <div class="quality-grid">
              <button v-for="quality in qualities" :key="quality.value" :class="{ selected: form.quality === quality.value }" @click="form.quality = quality.value"><b>{{ quality.label }}</b><span>{{ formatCost(quality.price) }} 算力</span><small>{{ quality.description }}</small></button>
            </div>
          </div>
          <div class="field">
            <span>画面比例</span>
            <div class="ratio-grid">
              <button v-for="ratio in ratios" :key="ratio.value" :class="{ selected: form.ratio === ratio.value }" @click="form.ratio = ratio.value">
                <i :style="ratio.style"></i><b>{{ ratio.label }}</b><small>{{ selectedSize(ratio.value) }}</small>
              </button>
            </div>
          </div>
          <div class="cost-summary"><span>{{ selectedQuality.label }} · 单张</span><strong>{{ formattedTotalCost }} 算力</strong></div>
          <button class="generate-button" :disabled="generating" @click="generate">
            <span v-if="generating" class="spinner"></span><span v-else>✦</span>{{ generating ? (form.mode === 'edit' ? '正在编辑...' : '正在生成...') : `${form.mode === 'edit' ? '立即编辑' : '立即生成'} · ${formattedTotalCost} 算力` }}
          </button>
          <div v-if="generateError" class="generate-error" role="alert">{{ generateError }}</div>
        </div>

        <div class="canvas-panel">
          <div v-if="!results.length && !generating" class="empty-state">
            <div class="empty-visual"><span>✦</span><i></i><b></b></div>
            <h2>让想象成为作品</h2>
            <p>输入画面描述并设置参数，AI 将在这里呈现你的创意。</p>
            <div class="examples"><button v-for="item in promptExamples" :key="item.title" @click="usePrompt(item.prompt)"><span>{{ item.icon }}</span><b>{{ item.title }}</b><small>{{ item.desc }}</small></button></div>
          </div>
          <div v-else-if="generating" class="generating-state"><div class="scan-box"><span>✦</span></div><h2>正在构筑画面</h2><p>AI 正在理解你的创意，请稍候</p></div>
          <div v-else class="result-area">
            <div class="result-head"><div><small>生成结果</small><h2>{{ results.length }} 张作品已完成</h2></div><button class="button ghost" @click="results = []">新建创作</button></div>
            <div class="result-grid" :class="`count-${results.length}`">
              <article v-for="(item, index) in results" :key="item.id || item.url || index">
                <img :src="assetUrl(item.url || item.image, results.length === 1 ? 1200 : 800)" :alt="form.prompt" />
                <div class="image-actions"><button title="下载作品" @click="download(item)">↓</button><button title="在新窗口查看" @click="preview(item)">↗</button></div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section v-else-if="page === 'gallery'" class="content-section gallery-section">
        <div class="section-toolbar"><div><span class="eyebrow">创作档案</span><h2>我的作品</h2><p>浏览、下载和管理你生成的所有图像。</p></div><button class="button primary" @click="go('create')">✦ 开始创作</button></div>
        <div v-if="galleryLoading" class="loading-list"><span class="spinner"></span>正在载入作品...</div>
        <div v-else-if="!works.length" class="gallery-empty"><span>▦</span><h3>还没有作品</h3><p>完成首次 AI 创作后，作品会自动保存在这里。</p><button class="button primary" @click="go('create')">创建第一张作品</button></div>
        <div v-else class="gallery-grid">
          <article v-for="work in works" :key="work.id">
            <div class="work-image"><img :src="assetUrl(work.url || work.image, 480)" :alt="work.prompt || 'AI 作品'" /><div><button @click="download(work)" title="下载">↓</button><button @click="removeWork(work)" title="删除">×</button></div></div>
            <p>{{ work.prompt || '未命名创作' }}</p><small>{{ work.createtime_text || work.createtime || '' }} · {{ work.model || 'AI' }}</small>
          </article>
        </div>
        <div v-if="galleryTotal > pageSize" class="pagination"><button :disabled="galleryPage === 1" @click="changeGalleryPage(-1)">‹</button><button v-for="n in totalPages" :key="n" :class="{ active: galleryPage === n }" @click="setGalleryPage(n)">{{ n }}</button><button :disabled="galleryPage === totalPages" @click="changeGalleryPage(1)">›</button></div>
      </section>

      <section v-else class="content-section pricing-section">
        <div class="pricing-intro"><span class="eyebrow">灵活计费</span><h2>为你的创意补充算力</h2><p>一次充值，永久有效。根据创作需求选择合适的算力包。</p></div>
        <div v-if="plansLoading" class="loading-list"><span class="spinner"></span>正在载入套餐...</div>
        <div v-else-if="plans.length" class="plans">
          <article v-for="(plan, index) in plans" :key="plan.id || index" :class="{ featured: plan.recommended }">
            <span v-if="plan.recommended" class="recommend">推荐</span><small>{{ plan.name }}</small><div class="price"><span>¥</span><strong>{{ plan.price }}</strong></div><p>{{ plan.description || '算力永久有效，可用于后台配置模型' }}</p><div class="plan-credit"><b>{{ plan.credits }}</b><span>创作算力</span></div><button class="button" :class="plan.recommended ? 'primary' : 'ghost'" @click="recharge(plan)">选择方案</button>
          </article>
        </div>
        <div v-else class="plans-unavailable"><h3>套餐暂不可用</h3><p>价格方案加载失败，请稍后刷新重试。</p><button class="button ghost" @click="loadPlans">重新加载</button></div>
        <div class="custom-recharge">
          <div><span class="eyebrow">自定义充值</span><h3>按需补充算力</h3><p>最低充值 1 元，充值金额与到账算力等额。</p></div>
          <div class="custom-recharge-form"><label><span>¥</span><input v-model.trim="customAmount" type="text" min="1" max="99999999.99" step="0.01" inputmode="decimal" placeholder="输入充值金额" /></label><small>到账 {{ customCredit }} 算力</small><button class="button primary" @click="rechargeCustom">立即充值</button></div>
        </div>
        <div class="pricing-notes"><span>✓ 算力永久有效</span><span>✓ 支持后台配置模型</span><span>✓ 作品高清下载</span><span>✓ 支付安全保障</span></div>
      </section>
    </main>

    <div v-if="authVisible" class="modal-backdrop" @click.self="authVisible = false">
      <div class="modal auth-modal">
        <button class="modal-close" @click="authVisible = false">×</button><div class="modal-brand"><span class="brand-mark">A</span></div>
        <h2>{{ authMode === 'login' ? '欢迎回来' : '创建创作账户' }}</h2><p>{{ authMode === 'login' ? '登录以继续你的 AI 创作之旅' : '注册账户以开始 AI 创作' }}</p>
        <form @submit.prevent="submitAuth">
          <label><span>{{ authMode === 'login' ? '账号' : '用户名' }}</span><input v-model.trim="authForm.username" autocomplete="username" :placeholder="authMode === 'login' ? '输入用户名' : '设置用户名'" required /></label>
          <label><span>密码</span><input v-model="authForm.password" type="password" :autocomplete="authMode === 'login' ? 'current-password' : 'new-password'" minlength="6" placeholder="至少 6 位字符" required /></label>
          <button class="button primary auth-submit" :disabled="authLoading">{{ authLoading ? '请稍候...' : authMode === 'login' ? '登录' : '完成注册' }}</button>
        </form>
        <div class="auth-switch">{{ authMode === 'login' ? '还没有账号？' : '已有账号？' }}<button @click="switchAuth">{{ authMode === 'login' ? '立即注册' : '返回登录' }}</button></div>
      </div>
    </div>

    <div v-if="payVisible" class="modal-backdrop" @click.self="closePay">
      <div class="modal pay-modal"><button class="modal-close" @click="closePay">×</button><span class="eyebrow">扫码支付</span><h2>充值 {{ selectedPlan.name }}</h2><div class="pay-amount">¥ {{ selectedPlan.price }}</div><div class="qr-wrap"><img v-if="payOrder.qrcode" :src="assetUrl(payOrder.qrcode)" alt="充值二维码" /><div v-else class="qr-loading"><span class="spinner"></span></div></div><p>请使用微信扫码支付</p><small>订单号：{{ payOrder.order_no || '创建中...' }}</small><div class="pay-status"><i></i>{{ payStatus }}</div></div>
    </div>

    <div v-if="toast.text" class="toast" :class="toast.type">{{ toast.text }}</div>
  </div>
</template>

<script>
import { api, assetUrl } from './services/api'

const qualityDefaults = [
  { label: '1K', value: '1K', price: 1, description: '标准清晰度' },
  { label: '2K', value: '2K', price: 2, description: '高清细节' },
  { label: '4K', value: '4K', price: 4, description: '超清输出' }
]

const sizeMap = {
  '1K': { '1:1': '1024x1024', '16:9': '1024x576', '9:16': '576x1024' },
  '2K': { '1:1': '2048x2048', '16:9': '2048x1152', '9:16': '1152x2048' },
  '4K': { '1:1': '4096x4096', '16:9': '4096x2304', '9:16': '2304x4096' }
}

export default {
  name: 'App',
  data() {
    return {
      page: 'create', user: null, authVisible: false, authMode: 'login', authLoading: false,
      authForm: { username: '', password: '' },
      form: { mode: 'generate', prompt: '', quality: '1K', ratio: '1:1' },
      modes: [{ label: '图片生成', value: 'generate' }, { label: '图片编辑', value: 'edit' }],
      sourceImage: null, imagePreview: '',
      qualities: qualityDefaults.map(item => ({ ...item })),
      priceMatrix: null,
      ratios: [{ label: '方形', value: '1:1', style: { width: '18px', height: '18px' } }, { label: '横向', value: '16:9', style: { width: '24px', height: '14px' } }, { label: '纵向', value: '9:16', style: { width: '14px', height: '24px' } }],
      promptExamples: [{ icon: '◈', title: '建筑空间', desc: '未来主义与光影', prompt: '极简未来主义建筑空间，巨大的弧形混凝土结构，柔和天光，静谧氛围，建筑摄影，高细节' }, { icon: '◎', title: '人物肖像', desc: '电影感人像', prompt: '电影感人物肖像，侧逆光，细腻皮肤质感，浅景深，35mm 胶片色彩，专业摄影' }, { icon: '⌁', title: '自然景观', desc: '壮阔与诗意', prompt: '清晨云海中的雪山，金色阳光穿透薄雾，史诗感构图，超广角风光摄影，真实细节' }],
      generating: false, generateError: '', results: [], works: [], galleryPage: 1, pageSize: 8, galleryTotal: 0, galleryLoading: false,
      plans: [], plansLoading: false, customAmount: '', payVisible: false, selectedPlan: {}, payOrder: {}, payStatus: '等待支付', payTimer: null,
      toast: { text: '', type: 'success' }, toastTimer: null
    }
  },
  computed: {
    pageTitle() { return { create: '创作台', gallery: '作品相册', pricing: '价格与算力' }[this.page] },
    displayName() { return (this.user && (this.user.nickname || this.user.username)) || '创作者' },
    totalPages() { return Math.max(1, Math.ceil(this.galleryTotal / this.pageSize)) },
    selectedQuality() { return this.qualities.find(item => item.value === this.form.quality) || this.qualities[0] },
    totalCost() { return Number(this.selectedQuality.price || 0) },
    formattedTotalCost() { return this.formatCost(this.totalCost) },
    customCredit() {
      const amount = Number(this.customAmount)
      return Number.isFinite(amount) && amount >= 1 ? amount.toFixed(2) : '0.00'
    }
  },
  created() {
    const saved = localStorage.getItem('ai_user')
    if (saved) { try { this.user = JSON.parse(saved) } catch (e) { localStorage.removeItem('ai_user') } }
    window.addEventListener('auth-expired', this.authExpired)
    window.addEventListener('focus', this.refreshProfile)
    this.loadConfig()
    this.refreshProfile()
  },
  beforeDestroy() { window.removeEventListener('auth-expired', this.authExpired); window.removeEventListener('focus', this.refreshProfile); clearInterval(this.payTimer); if (this.imagePreview) URL.revokeObjectURL(this.imagePreview) },
  methods: {
    assetUrl,
    go(page) { this.page = page; window.scrollTo({ top: 0, behavior: 'smooth' }); if (page === 'gallery') this.loadGallery(); if (page === 'pricing') this.loadPlans() },
    openAuth(mode) { this.authMode = mode; this.authVisible = true },
    switchAuth() { this.authMode = this.authMode === 'login' ? 'register' : 'login' },
    notify(text, type = 'success') { clearTimeout(this.toastTimer); this.toast = { text, type }; this.toastTimer = setTimeout(() => { this.toast.text = '' }, 3000) },
    authExpired() { this.user = null; localStorage.removeItem('ai_token'); localStorage.removeItem('ai_user'); this.openAuth('login'); this.notify('登录已过期，请重新登录', 'error') },
    async refreshProfile() {
      if (!this.user || !localStorage.getItem('ai_token')) return
      try {
        const data = await api.profile()
        if (data.userinfo) this.updateUser(data.userinfo)
        else if (data.money !== undefined) this.updateUser({ money: data.money })
      } catch (e) { /* 鉴权失效由请求层统一处理 */ }
    },
    async submitAuth() {
      this.authLoading = true
      try {
        const data = this.authMode === 'login' ? await api.login({ account: this.authForm.username, password: this.authForm.password }) : await api.register(this.authForm)
        this.user = data.userinfo
        localStorage.setItem('ai_token', data.userinfo.token)
        localStorage.setItem('ai_user', JSON.stringify(data.userinfo))
        this.authVisible = false
        this.notify(this.authMode === 'login' ? '登录成功' : '注册成功，欢迎加入')
      } catch (e) { this.notify(e.message, 'error') } finally { this.authLoading = false }
    },
    async logout() { try { await api.logout() } catch (e) { /* local logout remains valid */ } this.user = null; localStorage.removeItem('ai_token'); localStorage.removeItem('ai_user'); this.notify('已退出登录') },
    async loadConfig() {
      try {
        const data = await api.config()
        const prices = data.prices || data.quality_prices || data.resolution_prices
        if (prices && (prices.generate || prices.edit)) {
          this.priceMatrix = prices
          this.applyModePrices()
        } else if (prices) this.qualities = this.qualities.map(item => ({ ...item, price: Number(prices[item.value] ?? prices[item.value.toLowerCase()] ?? item.price) }))
        else if (data.cost !== undefined) this.qualities = this.qualities.map((item, index) => ({ ...item, price: Number(data.cost) * Math.pow(2, index) }))
      } catch (e) { /* defaults keep the editor usable */ }
    },
    setMode(mode) { this.form.mode = mode; this.results = []; this.applyModePrices() },
    applyModePrices() {
      if (!this.priceMatrix || !this.priceMatrix[this.form.mode]) return
      const prices = this.priceMatrix[this.form.mode]
      this.qualities = this.qualities.map(item => ({ ...item, price: Number(prices[item.value] ?? prices[item.value.toLowerCase()] ?? item.price) }))
    },
    selectImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        event.target.value = ''
        return this.notify('请选择 10MB 以内的 JPG、PNG 或 WEBP 图片', 'error')
      }
      if (this.imagePreview) URL.revokeObjectURL(this.imagePreview)
      this.sourceImage = file
      this.imagePreview = URL.createObjectURL(file)
    },
    formatCost(value) { return Number(value).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') },
    selectedSize(ratio = this.form.ratio) { return sizeMap[this.form.quality][ratio].replace('x', ' × ') },
    randomPrompt() { const item = this.promptExamples[Math.floor(Math.random() * this.promptExamples.length)]; this.form.prompt = item.prompt },
    usePrompt(prompt) { this.form.prompt = prompt },
    async generate() {
      if (!this.user) return this.openAuth('login')
      this.generateError = ''
      if (this.form.prompt.length < 3) return this.notify('请先输入更完整的创意描述', 'error')
      if (this.form.mode === 'edit' && !this.sourceImage) return this.notify('请先上传待编辑图片', 'error')
      await this.refreshProfile()
      if (Number(this.user.money || 0) < this.totalCost) {
        this.generateError = `算力余额不足，当前可用 ${this.formatCost(this.user.money || 0)} 算力，本次需要 ${this.formattedTotalCost} 算力`
        return this.notify(this.generateError, 'error')
      }
      this.generating = true
      const payload = { prompt: this.form.prompt, resolution: this.form.quality, ratio: this.form.ratio }
      try {
        const data = this.form.mode === 'edit' ? await api.edit(payload, this.sourceImage) : await api.generate(payload)
        this.results = Array.isArray(data) ? data : (data.images || data.list || [])
        if (data.userinfo) this.updateUser(data.userinfo)
        else if (data.money !== undefined) this.updateUser({ money: data.money })
        this.notify(this.form.mode === 'edit' ? '图片编辑完成' : '作品生成完成')
      } catch (e) {
        this.generateError = e.message || '生成失败，请稍后重试'
        this.notify(this.generateError, 'error')
        await this.refreshProfile()
      } finally { this.generating = false }
    },
    updateUser(user) { this.user = { ...this.user, ...user }; localStorage.setItem('ai_user', JSON.stringify(this.user)) },
    preview(item) { window.open(assetUrl(item.url || item.image), '_blank', 'noopener') },
    download(item) {
      const link = document.createElement('a')
      link.href = api.downloadUrl(item.id)
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    async loadGallery() {
      if (!this.user) return this.openAuth('login')
      this.galleryLoading = true
      try { const data = await api.gallery({ page: this.galleryPage, limit: this.pageSize }); this.works = data.list || data.rows || []; this.galleryTotal = Number(data.total || this.works.length) } catch (e) { this.notify(e.message, 'error') } finally { this.galleryLoading = false }
    },
    setGalleryPage(page) { this.galleryPage = page; this.loadGallery() },
    changeGalleryPage(offset) { this.setGalleryPage(this.galleryPage + offset) },
    async removeWork(work) { if (!window.confirm('确定删除这张作品吗？')) return; try { await api.removeWork(work.id); this.notify('作品已删除'); this.loadGallery() } catch (e) { this.notify(e.message, 'error') } },
    async loadPlans() {
      this.plansLoading = true
      this.plans = []
      try {
        const data = await api.plans()
        const list = Array.isArray(data) ? data : (data.list || data.rows || [])
        this.plans = list.filter(plan => plan && plan.id).map(plan => ({ ...plan, price: plan.price ?? plan.amount, credits: plan.credits ?? plan.credit }))
      } catch (e) { this.notify(e.message, 'error') } finally { this.plansLoading = false }
    },
    async recharge(plan) {
      if (!this.user) return this.openAuth('login')
      this.selectedPlan = plan; this.payOrder = {}; this.payStatus = '正在创建订单'; this.payVisible = true
      try { this.payOrder = await api.createRecharge({ plan_id: plan.id, pay_type: 'qrcode' }); this.payStatus = '等待支付'; if (this.payOrder.order_no) this.startPayPolling() } catch (e) { this.payVisible = false; this.notify(e.message, 'error') }
    },
    async rechargeCustom() {
      if (!this.user) return this.openAuth('login')
      if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(this.customAmount) || Number(this.customAmount) < 1) return this.notify('充值金额最低1元，最多保留两位小数', 'error')
      const amount = Number(this.customAmount).toFixed(2)
      this.selectedPlan = { name: '自定义充值', price: amount, credits: amount }; this.payOrder = {}; this.payStatus = '正在创建订单'; this.payVisible = true
      try { this.payOrder = await api.createRecharge({ amount, pay_type: 'qrcode' }); this.payStatus = '等待支付'; if (this.payOrder.order_no) this.startPayPolling() } catch (e) { this.payVisible = false; this.notify(e.message, 'error') }
    },
    startPayPolling() { clearInterval(this.payTimer); this.payTimer = setInterval(async () => { try { const data = await api.rechargeStatus(this.payOrder.order_no); if (data.paid || data.status === 'paid') { this.payStatus = '支付成功'; clearInterval(this.payTimer); if (data.userinfo) this.updateUser(data.userinfo); this.notify('充值成功'); setTimeout(this.closePay, 1200) } } catch (e) { /* continue polling transient failures */ } }, 2500) },
    closePay() { clearInterval(this.payTimer); this.payVisible = false }
  }
}
</script>

<style src="./assets/app.css"></style>
